import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { DEFAULT_LANG, HTML_LANG, LANG_STORAGE_KEY, type Lang, type MessageKey } from './messages'

/**
 * Wrapper fino sobre o react-i18next, mantendo a API `{ lang, setLang, t }` que o
 * app já usa. `t(key)` ou `t(key, { nome })` (interpolação i18next `{{nome}}`).
 * `useTranslation` re-renderiza os consumidores ao trocar de idioma.
 */
export interface UseI18n {
  lang: Lang
  setLang: (lang: Lang) => void
  t: (key: MessageKey, params?: Record<string, string | number>) => string
}

const SUPPORTED: Lang[] = ['pt', 'en', 'es']

export function useI18n(): UseI18n {
  const { t, i18n } = useTranslation()
  const resolved = i18n.resolvedLanguage ?? i18n.language
  const lang = (SUPPORTED as string[]).includes(resolved) ? (resolved as Lang) : DEFAULT_LANG

  const setLang = useCallback(
    (next: Lang) => {
      void i18n.changeLanguage(next)
      try {
        localStorage.setItem(LANG_STORAGE_KEY, next)
      } catch {
        /* ignore */
      }
      try {
        document.documentElement.setAttribute('lang', HTML_LANG[next])
      } catch {
        /* ignore */
      }
    },
    [i18n],
  )

  return { lang, setLang, t: t as UseI18n['t'] }
}
